import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Search } from "lucide-react";

import { listProducts } from "../../actions/productActions";
import Loader from "../../components/Loader";
import Meta from "../../components/Meta";
import ProductCard from "../../components/ProductCard";
import {
  HABERDASHERY_SHOP_KEY,
  READY_MADE_SHOP_KEY,
  SHOP_PATHS,
  TEXTILE_SHOP_KEY,
} from "../../constants/shopMeta";

import "./SearchScreen.css";

function safeText(value = "") {
  return String(value ?? "").trim();
}

export default function SearchScreen() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = safeText(searchParams.get("q"));
  const [draft, setDraft] = useState(query);
  const productList = useSelector((state) => state.productList || {});
  const {
    loading = false,
    error = "",
    products = [],
    count = 0,
  } = productList;

  useEffect(() => {
    setDraft(query);
  }, [query]);

  useEffect(() => {
    if (!query) return;

    dispatch(
      listProducts({
        search: query,
        pageNumber: 1,
        pageSize: 24,
      }),
    ).catch(() => {});
  }, [dispatch, query]);

  const resultCopy = useMemo(() => {
    if (!query) return "Search across Academy Costumes London.";
    if (loading) return "Searching...";
    return `${Number(count || products.length)} result${
      Number(count || products.length) === 1 ? "" : "s"
    } for "${query}"`;
  }, [count, loading, products.length, query]);

  const submitSearch = useCallback(
    (event) => {
      event.preventDefault();

      const next = safeText(draft);
      const params = new URLSearchParams();

      if (next) params.set("q", next);
      setSearchParams(params, { replace: false });
    },
    [draft, setSearchParams],
  );

  return (
    <main className="search-screen">
      <Meta
        title="Search | Academy Costumes London"
        description="Search Academy Costumes London products."
      />

      <header className="search-screen__hero">
        <p>Academy Costumes London</p>
        <h1>Search</h1>
        <span>{resultCopy}</span>
      </header>

      <form className="search-screen__form" onSubmit={submitSearch}>
        <Search size={19} aria-hidden="true" />
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Search products, materials, trims or garments"
          aria-label="Search products"
        />
        <button type="submit">Search</button>
      </form>

      {loading ? <Loader /> : null}

      {error ? <section className="search-screen__empty">{error}</section> : null}

      {!query ? (
        <section className="search-screen__empty">
          <p>Start with a product name, material, colour, SKU or garment type.</p>
          <div>
            <Link to={SHOP_PATHS[HABERDASHERY_SHOP_KEY]}>Haberdashery</Link>
            <Link to={SHOP_PATHS[TEXTILE_SHOP_KEY]}>Textiles</Link>
            <Link to={SHOP_PATHS[READY_MADE_SHOP_KEY]}>Ready-Made</Link>
          </div>
        </section>
      ) : !loading && !products.length && !error ? (
        <section className="search-screen__empty">
          <p>No products matched your search.</p>
          <Link to={SHOP_PATHS[HABERDASHERY_SHOP_KEY]}>Browse shops</Link>
        </section>
      ) : (
        <section className="search-screen__grid">
          {products.map((product) => (
            <ProductCard
              key={product._id || product.id}
              product={product}
              imageSizes="(max-width: 620px) calc(100vw - 24px), (max-width: 980px) calc(50vw - 25px), min(25vw, 302px)"
            />
          ))}
        </section>
      )}
    </main>
  );
}
